import { EventDispatcher } from '../EventDispatcher';

/**
 * TranslationService provides translation capabilities within Potree.
 *
 * React injects a source-string-keyed translation map via setLanguageData().
 * Potree code calls t('source string') to get the translated string.
 *
 * Dispatches 'language_changed' when the language or translations are updated,
 * so Potree components can re-render translated text.
 */
export class TranslationService extends EventDispatcher {
  private _language = 'en';
  private _translations: Record<string, string> = {};

  get language(): string {
    return this._language;
  }

  /**
   * Translate a source string. Returns the translation if available,
   * otherwise falls back to the source string itself.
   *
   * `args` replaces `{placeholder}` tokens in the translated string,
   * mirroring the `args` option of the React `useT` hook.
   */
  t(source: string, options?: { swc?: boolean; args?: Record<string, string | number> }): string {
    let result = this._translations[source] || source;
    if (options?.args) {
      for (const key of Object.keys(options.args)) {
        result = result.replace(`{${key}}`, String(options.args[key]));
      }
    }
    if (options?.swc && result.length > 0) {
      result = result[0].toUpperCase() + result.slice(1);
    }
    return result;
  }

  /**
   * Update translation data. Called by React when language changes.
   * @param language - Language code (e.g. 'en', 'de')
   * @param translations - Map of source string → translated string
   */
  setLanguageData(language: string, translations: Record<string, string>): void {
    this._language = language;
    this._translations = translations;
    this.dispatchEvent({ type: 'language_changed', language });
  }
}
